import React, { useContext, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Ripple } from 'primereact/ripple';
import { classNames } from 'primereact/utils';
import { Badge } from 'primereact/badge';
import { LayoutContext } from './context/layoutcontext';
import { MenuContext } from './context/menucontext';
import { useSubmenuOverlayPosition } from './hooks/useSubmenuOverlayPosition';

const AppMenuitem = ({ item, index, className, parentKey, root }) => {
    const { activeMenu, setActiveMenu } = useContext(MenuContext);
    const { isSlim, isCompact, isHorizontal, isDesktop, setLayoutState, layoutState, layoutConfig } = useContext(LayoutContext);
    const location = useLocation();
    const submenuRef = useRef(null);
    const menuitemRef = useRef(null);
    const key = parentKey ? parentKey + '-' + index : String(index);
    const isActiveRoute = item.to && location.pathname === item.to;
    const active = activeMenu === key || !!(activeMenu && activeMenu.startsWith(key + '-'));
    
    // NOTE: there used to be an "auto-expand in static mode" effect here. It was
    // removed because it forced the open section shut on every navigation.
    //
    // Why it broke: `item.items` was in its dependency array, and AppMenu rebuilds
    // that array on every render (injectNotificationsItem spreads `[...group.items]`).
    // A new array reference each render meant the effect re-ran constantly, and
    // EVERY root section called setActiveMenu(its own key) — last one wins. Since
    // `activeMenu` holds a single key, that clobbered whichever section the user
    // had open. See P14 in the ui-ux skill: never put an unmemoized object/array
    // in a dependency array.
    //
    // Nothing replaces it: the route effect below already opens the section that
    // contains the current URL, and a parent stays open while any descendant is
    // active via the `startsWith(key + '-')` check above.

    useSubmenuOverlayPosition({
        target: menuitemRef.current,
        overlay: submenuRef.current,
        container: menuitemRef.current && menuitemRef.current.closest('.layout-menu-container'),
        when: root && active && (isSlim() || isCompact() || isHorizontal()) && isDesktop()
    });

    useEffect(() => {
        if (layoutState.resetMenu) {
            setActiveMenu('');
            setLayoutState((prevLayoutState) => ({
                ...prevLayoutState,
                resetMenu: false
            }));
        }
    }, [layoutState]);

    useEffect(() => {
        if (!(isSlim() || isHorizontal() || isCompact()) && isActiveRoute) {
            setActiveMenu(key);
        }
    }, [layoutConfig]);

    useEffect(() => {
        const onRouteChange = (url) => {
            if (!(isSlim() || isHorizontal() || isCompact()) && item.to && item.to === url) {
                setActiveMenu(key);
            }
        };
        onRouteChange(location.pathname);
    }, [location]);

    const itemClick = (event) => {
        //avoid processing disabled items
        if (item.disabled) {
            event.preventDefault();
            return;
        }

        // Diamond mobile touch optimization - only prevent for dropdown items
        if (!isDesktop() && item.items) {
            event.preventDefault();
            event.stopPropagation();
        }

        // navigate with hover
        if (root && (isSlim() || isHorizontal() || isCompact())) {
            const isSubmenu = event.currentTarget.closest('.layout-root-menuitem.active-menuitem > ul') !== null;
            if (isSubmenu)
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    menuHoverActive: true
                }));
            else
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    menuHoverActive: !prevLayoutState.menuHoverActive
                }));
        }

        //execute command
        if (item.command) {
            item.command({ originalEvent: event, item: item });
        }

        // toggle active state
        if (item.items) {
            setActiveMenu(active ? parentKey : key);

            if (root && !active && (isSlim() || isHorizontal() || isCompact())) {
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    overlaySubmenuActive: true
                }));
            }
        } else {
            if (!isDesktop()) {
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    staticMenuMobileActive: !prevLayoutState.staticMenuMobileActive
                }));
            }

            if (isSlim() || isHorizontal() || isCompact()) {
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    menuHoverActive: false
                }));
            }

            setActiveMenu(key);
        }
    };

    const onMouseEnter = () => {
        // activate item on hover
        if (root && (isSlim() || isHorizontal() || isCompact()) && isDesktop()) {
            if (!active && layoutState.menuHoverActive) {
                setActiveMenu(key);
            }
        }
    };

    const subMenu =
        item.items && item.visible !== false ? (
            <ul ref={submenuRef}>
                {item.items.map((child, i) => {
                    return <AppMenuitem item={child} index={i} className={child.badgeClass} parentKey={key} key={`${key}-${i}`} />;
                })}
            </ul>
        ) : null;

    return (
        <li
            ref={menuitemRef}
            className={classNames({
                'layout-root-menuitem': root,
                'active-menuitem': active
            })}
        >
            {root && item.visible !== false && !isHorizontal() && item.label && <div className="layout-menuitem-root-text">{item.label}</div>}
            {(!item.to || item.items) && item.visible !== false && (item.label || item.icon) ? (
                <>
                    <a
                        href={item.url || undefined}
                        role={item.url ? undefined : 'button'}
                        onClick={(e) => { if (!item.url) e.preventDefault(); itemClick(e); }}
                        className={classNames(
                            item.class,
                            'p-ripple tooltip-target',
                            {
                                'p-disabled': item.disabled,
                                'layout-menuitem-mobile': !isDesktop()
                            }
                        )}
                        data-pr-tooltip={item.title || item.label}
                        data-pr-disabled={!(isSlim() && root && !layoutState.menuHoverActive)}
                        tabIndex={0}
                        onMouseEnter={onMouseEnter}
                        title={item.title || item.label}
                    >
                        <i className={classNames('layout-menuitem-icon', item.icon)}></i>
                        <span className="layout-menuitem-text">{item.label}</span>
                        {item.badge && <Badge value={item.badge} severity={item.badgeSeverity} />}
                        {item.items && <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>}
                        <Ripple />
                    </a>
                </>
            ) : null}

            {item.to && !item.items && item.visible !== false ? (
                <>
                    <Link
                        to={item.to}
                        replace={item.replaceUrl}
                        onClick={(e) => itemClick(e)}
                        className={classNames(
                            item.class,
                            'p-ripple',
                            {
                                'active-route': isActiveRoute,
                                'active': isActiveRoute,
                                'p-disabled': item.disabled,
                                'layout-menuitem-mobile': !isDesktop()
                            }
                        )}
                        aria-current={isActiveRoute ? 'page' : undefined}
                        tabIndex={0}
                        onMouseEnter={onMouseEnter}
                        title={item.title || item.label}
                    >
                        <i className={classNames('layout-menuitem-icon', item.icon)}></i>
                        <span className="layout-menuitem-text">{item.label}</span>
                        {item.badge && <Badge value={item.badge} severity={item.badgeSeverity} />}
                        {item.items && <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>}
                        <Ripple />
                    </Link>
                </>
            ) : null}
            {subMenu}
        </li>
    );
};

export default AppMenuitem;